import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Plus, Pencil, Trash2, Save, X, Power, PowerOff, Tag, Calendar, Percent, DollarSign
} from "lucide-react";

const STATUS_STYLE = {
  live:       { color: "#D4FF00", bg: "rgba(212,255,0,0.15)", label: "Live" },
  scheduled:  { color: "#00F0FF", bg: "rgba(0,240,255,0.15)", label: "Scheduled" },
  expired:    { color: "#A1A1AA", bg: "rgba(161,161,170,0.15)", label: "Expired" },
  inactive:   { color: "#A1A1AA", bg: "rgba(161,161,170,0.15)", label: "Paused" },
  exhausted:  { color: "#FF007F", bg: "rgba(255,0,127,0.15)", label: "Exhausted" },
};

function emptyDraft() {
  return {
    code: "", title: "", discount_pct: "", discount_amt: "",
    min_order: "0", max_uses: "1000",
    start_at: "", end_at: "", active: true,
    applicable_products: [], applicable_categories: [], store_id: "",
  };
}

export default function CouponsManager({ scope = "seller", stores = [], categories = [], products = [] }) {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null); // draft or null
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");

  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(""); } else { setMsg(m); setErr(""); }
    setTimeout(() => { setMsg(""); setErr(""); }, 2400);
  };

  const endpoint = scope === "admin" ? "/admin/coupons" : "/seller/coupons";

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setCoupons(data);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Load failed", true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const filtered = useMemo(() => {
    if (!filter) return coupons;
    return coupons.filter(c => (c.live_status || "live") === filter);
  }, [coupons, filter]);

  const save = async () => {
    if (!editing) return;
    const d = editing;
    const payload = {
      code: d.code, title: d.title || d.code,
      discount_pct: d.discount_pct === "" ? null : Number(d.discount_pct),
      discount_amt: d.discount_amt === "" ? null : Number(d.discount_amt),
      min_order: d.min_order === "" ? 0 : Number(d.min_order),
      max_uses: d.max_uses === "" ? 0 : Number(d.max_uses),
      start_at: d.start_at || null,
      end_at: d.end_at || null,
      active: !!d.active,
      applicable_products: d.applicable_products || [],
      applicable_categories: d.applicable_categories || [],
      store_id: d.store_id || null,
    };
    try {
      if (d._editing) {
        await api.put(`/seller/coupons/${d.code}`, payload);
        flash("Coupon updated.");
      } else {
        await api.post(`/seller/coupons`, payload);
        flash("Coupon created · live now.");
      }
      setEditing(null);
      load();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Save failed", true); }
  };

  const toggle = async (code) => {
    try {
      await api.post(`/seller/coupons/${code}/toggle`);
      flash("Status updated.");
      load();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Toggle failed", true); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/seller/coupons/${code}`);
      flash("Deleted.");
      load();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Delete failed", true); }
  };

  const startEdit = (c) => setEditing({
    code: c.code, title: c.title || "",
    discount_pct: c.discount_pct ?? "",
    discount_amt: c.discount_amt ?? "",
    min_order: c.min_order ?? "0",
    max_uses: c.max_uses ?? "0",
    start_at: c.start_at ? c.start_at.slice(0, 16) : "",
    end_at: c.end_at ? c.end_at.slice(0, 16) : "",
    active: c.active !== false,
    applicable_products: c.applicable_products || [],
    applicable_categories: c.applicable_categories || [],
    store_id: c.store_id || "",
    _editing: true,
  });

  return (
    <div className="space-y-3" data-testid="coupons-manager">
      <div className="card p-4 flex flex-wrap items-center gap-2 justify-between">
        <div>
          <div className="font-display font-bold uppercase text-sm">Coupons & rewards</div>
          <div className="text-xs text-[#A1A1AA] mt-1">
            Create discount codes, schedule them, and toggle live. {scope === "admin" ? "Admin sees every store." : "Yours only."}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input-dark text-xs" value={filter}
            onChange={e => setFilter(e.target.value)} data-testid="coupons-filter">
            <option value="">All statuses</option>
            {Object.entries(STATUS_STYLE).map(([k, s]) => (
              <option key={k} value={k}>{s.label}</option>
            ))}
          </select>
          {!editing && (
            <button onClick={() => setEditing({ ...emptyDraft(), store_id: stores[0]?.store_id || "" })}
              className="btn-primary text-xs px-3 py-2" data-testid="coupon-new-btn">
              <Plus size={12} className="inline mr-1"/> New coupon
            </button>
          )}
        </div>
      </div>

      {msg && <div className="text-sm text-[#D4FF00]" data-testid="coupon-msg">{msg}</div>}
      {err && <div className="text-sm text-[#FF007F]" data-testid="coupon-err">{err}</div>}

      {editing && (
        <CouponForm draft={editing} setDraft={setEditing}
          stores={stores} categories={categories} products={products}
          isAdmin={scope === "admin"}
          onSave={save} onCancel={() => setEditing(null)}/>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <Th>Code</Th>
              <Th>Discount</Th>
              <Th right>Min order</Th>
              <Th>Window</Th>
              <Th>Scope</Th>
              <Th right>Used</Th>
              <Th>Status</Th>
              <Th/>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const st = STATUS_STYLE[c.live_status] || STATUS_STYLE.live;
              const scopeLabel = (c.applicable_products?.length || c.applicable_categories?.length)
                ? `${c.applicable_products?.length || 0} prod · ${c.applicable_categories?.length || 0} cat`
                : "All";
              return (
                <tr key={c.code} className="border-t border-[#3D2845] hover:bg-[#3D2845]/30" data-testid={`coupon-row-${c.code}`}>
                  <td className="p-3">
                    <div className="font-mono-display font-black text-[#D4FF00]">{c.code}</div>
                    <div className="text-[10px] text-[#A1A1AA] line-clamp-1">{c.title || "—"}</div>
                  </td>
                  <td className="p-3">
                    {c.discount_pct ? (
                      <div className="flex items-center gap-1 font-bold"><Percent size={12}/> {c.discount_pct}%</div>
                    ) : c.discount_amt ? (
                      <div className="flex items-center gap-1 font-bold"><DollarSign size={12}/> {c.discount_amt}</div>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-right text-xs">${c.min_order || 0}</td>
                  <td className="p-3 text-xs">
                    {c.start_at ? new Date(c.start_at).toLocaleDateString() : "—"}
                    <span className="text-[#A1A1AA]"> → </span>
                    {c.end_at ? new Date(c.end_at).toLocaleDateString() : "∞"}
                  </td>
                  <td className="p-3 text-xs">{scopeLabel}</td>
                  <td className="p-3 text-right text-xs">{c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded"
                      style={{ color: st.color, background: st.bg }}>
                      {st.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => toggle(c.code)} title={c.active ? "Pause" : "Take live"}
                        data-testid={`coupon-toggle-${c.code}`}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.active ? "bg-[#D4FF00]/15 text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black" : "bg-[#3D2845] text-[#A1A1AA] hover:bg-[#D4FF00] hover:text-black"}`}>
                        {c.active ? <Power size={12}/> : <PowerOff size={12}/>}
                      </button>
                      <button onClick={() => startEdit(c)} title="Edit"
                        data-testid={`coupon-edit-${c.code}`}
                        className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center">
                        <Pencil size={12}/>
                      </button>
                      <button onClick={() => remove(c.code)} title="Delete"
                        data-testid={`coupon-delete-${c.code}`}
                        className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#A1A1AA]">No coupons here. Click "New coupon" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#A1A1AA] text-right">{filtered.length} coupon{filtered.length === 1 ? "" : "s"}</div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] ${right ? "text-right" : ""}`}>{children}</th>
);

function CouponForm({ draft, setDraft, stores, categories, products, isAdmin, onSave, onCancel }) {
  const set = (patch) => setDraft({ ...draft, ...patch });
  const toggleArr = (key, val) => {
    const arr = draft[key] || [];
    set({ [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };
  return (
    <div className="card p-5 grid gap-4 border-2 border-[#D4FF00]/40" data-testid="coupon-form">
      <div className="font-display font-bold uppercase text-sm flex items-center gap-2">
        <Tag size={14}/> {draft._editing ? `Edit coupon ${draft.code}` : "New coupon"}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <FieldLabel label="Code" required>
          <input className="input-dark uppercase" placeholder="e.g. NEON25"
            value={draft.code} disabled={!!draft._editing}
            onChange={e => set({ code: e.target.value.toUpperCase().replace(/\s/g, "") })}
            data-testid="coupon-code-input"/>
        </FieldLabel>
        <FieldLabel label="Title (shown to buyer)">
          <input className="input-dark" placeholder="Neon Friday 25%"
            value={draft.title} onChange={e => set({ title: e.target.value })}
            data-testid="coupon-title-input"/>
        </FieldLabel>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <FieldLabel label="Discount %">
          <input type="number" min={0} max={100} step={1} className="input-dark" placeholder="25"
            value={draft.discount_pct} onChange={e => set({ discount_pct: e.target.value, discount_amt: e.target.value ? "" : draft.discount_amt })}
            data-testid="coupon-pct-input"/>
        </FieldLabel>
        <FieldLabel label="OR · Discount $">
          <input type="number" min={0} step={0.01} className="input-dark" placeholder="15"
            value={draft.discount_amt} onChange={e => set({ discount_amt: e.target.value, discount_pct: e.target.value ? "" : draft.discount_pct })}
            data-testid="coupon-amt-input"/>
        </FieldLabel>
        <FieldLabel label="Min order $">
          <input type="number" min={0} step={1} className="input-dark"
            value={draft.min_order} onChange={e => set({ min_order: e.target.value })}
            data-testid="coupon-min-input"/>
        </FieldLabel>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <FieldLabel label="Max uses">
          <input type="number" min={0} step={1} className="input-dark"
            value={draft.max_uses} onChange={e => set({ max_uses: e.target.value })}
            data-testid="coupon-max-input"/>
        </FieldLabel>
        <FieldLabel label="Starts">
          <div className="relative">
            <Calendar size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"/>
            <input type="datetime-local" className="input-dark pl-8"
              value={draft.start_at} onChange={e => set({ start_at: e.target.value })}
              data-testid="coupon-start-input"/>
          </div>
        </FieldLabel>
        <FieldLabel label="Ends">
          <div className="relative">
            <Calendar size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"/>
            <input type="datetime-local" className="input-dark pl-8"
              value={draft.end_at} onChange={e => set({ end_at: e.target.value })}
              data-testid="coupon-end-input"/>
          </div>
        </FieldLabel>
      </div>

      {(stores.length > 1 || isAdmin) && (
        <FieldLabel label="Store">
          <select className="input-dark" value={draft.store_id || ""}
            onChange={e => set({ store_id: e.target.value })} data-testid="coupon-store-input">
            <option value="">{isAdmin ? "Platform-wide (no store)" : "—"}</option>
            {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
          </select>
        </FieldLabel>
      )}

      <FieldLabel label="Applies to categories (optional)">
        <div className="flex flex-wrap gap-2">
          {categories.map(c => {
            const on = (draft.applicable_categories || []).includes(c.category_id);
            return (
              <button key={c.category_id} type="button" onClick={() => toggleArr("applicable_categories", c.category_id)}
                data-testid={`coupon-cat-${c.category_id}`}
                className={`chip text-xs ${on ? "active" : ""}`}>{c.name}</button>
            );
          })}
        </div>
      </FieldLabel>

      {products.length > 0 && (
        <FieldLabel label="Specific products (optional)">
          <ProductPicker products={products}
            selectedCategories={draft.applicable_categories || []}
            selected={draft.applicable_products || []}
            onChange={(arr) => set({ applicable_products: arr })}/>
        </FieldLabel>
      )}

      <FieldLabel label="Status">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={!!draft.active} onChange={e => set({ active: e.target.checked })}
            data-testid="coupon-active-input" className="accent-[#D4FF00] w-4 h-4"/>
          {draft.active ? "Live · buyers can use this code" : "Paused · saved as draft"}
        </label>
      </FieldLabel>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="btn-secondary text-xs px-3 py-2" data-testid="coupon-cancel-btn">
          <X size={12} className="inline mr-1"/> Cancel
        </button>
        <button onClick={onSave} className="btn-primary text-xs px-4 py-2" data-testid="coupon-save-btn">
          <Save size={12} className="inline mr-1"/> {draft._editing ? "Save changes" : "Create & take live"}
        </button>
      </div>
    </div>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <div className="grid gap-1">
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">
        {label}{required && <span className="text-[#FF007F]"> *</span>}
      </label>
      {children}
    </div>
  );
}

function ProductPicker({ products, selected, onChange, selectedCategories }) {
  const [q, setQ] = useState("");
  const catScope = Array.isArray(selectedCategories) ? selectedCategories : [];
  const filtered = useMemo(() => {
    let pool = products;
    // When the user has selected categories above, scope the picker to
    // products within those categories — eliminates the "I need to scroll
    // through 200 unrelated products" friction reported by sellers.
    if (catScope.length) {
      pool = pool.filter(p => catScope.includes(p.category_id));
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return pool;
    return pool.filter(p => p.name.toLowerCase().includes(needle));
  }, [products, q, catScope]);
  const toggle = (pid) => onChange(selected.includes(pid) ? selected.filter(x => x !== pid) : [...selected, pid]);
  const scopedHint = catScope.length
    ? `Showing ${filtered.length} product${filtered.length === 1 ? "" : "s"} matching ${catScope.length} chosen categor${catScope.length === 1 ? "y" : "ies"}.`
    : `Showing all ${products.length} product${products.length === 1 ? "" : "s"} — pick categories above to narrow down.`;
  return (
    <div className="grid gap-2">
      <div className="text-[10px] text-[#A1A1AA]" data-testid="coupon-prod-scope-hint">{scopedHint}</div>
      <input className="input-dark py-1 text-xs" placeholder={`Search ${filtered.length} products…`}
        value={q} onChange={e => setQ(e.target.value)}
        data-testid="coupon-prod-search"/>
      <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1">
        {filtered.slice(0, 80).map(p => {
          const on = selected.includes(p.product_id);
          return (
            <button key={p.product_id} type="button" onClick={() => toggle(p.product_id)}
              data-testid={`coupon-prod-${p.product_id}`}
              className={`chip text-xs ${on ? "active" : ""}`}>{p.name}</button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-xs text-[#A1A1AA]">
            {q ? `No products match "${q}".` :
              (catScope.length ? "No products in the selected categor(y|ies) yet." : "No products.")}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="text-xs text-[#A1A1AA]">{selected.length} product{selected.length === 1 ? "" : "s"} selected</div>
      )}
    </div>
  );
}
